import {
  useEffect,
  useRef,
  useState,
} from 'react'

import acquireAccessToken from './acquireAccessToken'
import exchangeFlowToken from './exchangeFlowToken'
import hasAccessToken from './hasAccessToken'


/**
 * Acquires an access token before any protected request goes out, unless one
 * is already held.
 *
 * The token lives in memory only, so every page load starts without one. A
 * flow token, when one is given, is exchanged for it. Otherwise the first load
 * after a sign-in collects the token from the one-time handoff; any other
 * load, and a handoff that turns up empty, trades the refresh cookie for a new
 * one instead.
 *
 * Reports readiness rather than success. A visitor who has never signed in has
 * no refresh cookie, and one whose session has ended has a cookie the server
 * no longer accepts. Both carry on unauthenticated exactly as they would with
 * no token at all, and what that means is for the application to decide.
 *
 * A refused flow token is reported separately, because unlike a missing
 * cookie it means the sign-in that was attempted has failed.
 *
 * @param {Object} [args]
 * @param {String|null} [args.flowToken] - a one-time sign-in token
 *
 * @returns {Object} with the shape { isFlowTokenRejected, isReady }
 */
export default function useAccessTokenBootstrap({ flowToken } = {}) {
  // A token already held means an earlier mount on this page load has done
  // the work, so this one reports ready on its first render and gives up no
  // paint.
  const [isReady, setReady] = useState(hasAccessToken)
  const [isFlowTokenRejected, setFlowTokenRejected] = useState()

  // The flow token and the handoff are both single-use, so a second attempt
  // would be refused. A ref survives the effect running twice under
  // StrictMode, which an attempt started inside the effect alone would not.
  const acquisition = useRef()

  useEffect(() => {
    if ( isReady ) {
      return
    }

    // A flow token is the credential this attempt was given, so it is the only
    // one tried. A refresh sent alongside the exchange would race it and could
    // replace the session the exchange brings with another.
    const acquire = () => {
      if ( flowToken ) {
        return exchangeFlowToken(flowToken)
          .catch(() => {
            // Only reporting a failure can reject, so the exchange failed
            // either way.
            return false
          })
          .then((isExchanged) => {
            setFlowTokenRejected(!isExchanged)
          })
      }

      return acquireAccessToken()
    }

    if ( !acquisition.current ) {
      acquisition.current = acquire()
    }

    acquisition.current
      .catch(() => {
        // Swallowed, because a failed attempt leaves the visitor exactly where
        // having no token would, and there is nothing further to do about it
        // here.
      })
      .finally(() => {
        // Readiness is unconditional: it reports that the attempt is over, not
        // that a token was collected. Nothing above this renders until it
        // flips, so letting a rejection skip it would trade a missing token
        // for a blank application.
        setReady(true)
      })
  }, [flowToken, isReady])

  return {
    isFlowTokenRejected,
    isReady,
  }
}
